import React, { useState, useEffect } from 'react';
import { deriveCapabilities, resolvePostAuthRoute } from '@/lib/tenantCapabilities';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { motion } from 'framer-motion';
import { Mail, Loader2, Target, Users, Zap, TrendingUp } from 'lucide-react';
import { User } from '@/api/entities';
import { getTenantFromEmail, isPlatformAccessBlockedEmail } from '@/lib/tenantUtils';
import { NEOGTM_LOGO_PNG_URL } from '@/lib/brandAssets';

const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'https://api.neogtm.com'
  : 'https://api.neogtm.com';
const BLOCKED_ACCESS_MESSAGE = 'Access has been temporarily blocked by the administrator.';

export default function Landing() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLogin, setIsLogin] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  
  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [companyName, setCompanyName] = useState('');

  // Platform auth uses full-page redirect (no popup). Gmail/Outlook OAuth redirect to
  // /AuthCallback in the same tab, which then navigates to /Home. No postMessage listener needed.

  // Check if user is already authenticated and redirect to /Home
  // Skip redirect when coming from logout (Safari may not clear session cookie immediately)
  useEffect(() => {
    const checkAuthentication = async () => {
      const fromLogout = new URLSearchParams(location.search).has('logout');
      if (fromLogout) {
        setIsCheckingAuth(false);
        return;
      }

      try {
        const sessionResponse = await fetch(`${API_BASE_URL}/api/auth/session`, {
          credentials: 'include',
        });
        if (sessionResponse.ok) {
          const sessionData = await sessionResponse.json();
          if (sessionData?.user) {
            navigate(resolvePostAuthRoute(deriveCapabilities(sessionData.tenant)), { replace: true });
            return;
          }
        }
        const currentUser = await User.me();
        if (currentUser) {
          navigate('/Home', { replace: true });
        }
      } catch (error) {
        // User is not authenticated (or session check failed), show landing page.
        // Do not show blocked message from passive User.me checks.
        console.log('User not authenticated, showing landing page');
      } finally {
        setIsCheckingAuth(false);
      }
    };

    checkAuthentication();
  }, [navigate, location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const blocked = params.get('blocked');
    if (blocked === 'demarque' || blocked === 'smowltech' || blocked === 'platform') {
      setError(BLOCKED_ACCESS_MESSAGE);
    }
  }, [location.search]);

  const handleGmailAuth = async () => {
    try {
      setIsLoading(true);
      setError('');

      const tempUserId = 'auth_' + Date.now();

      localStorage.setItem('gmail_oauth_user_id', tempUserId);
      localStorage.setItem('gmail_oauth_auth_flow', 'true');

      const response = await fetch(
        `${API_BASE_URL}/api/auth/gmail/start?redirect_uri=${encodeURIComponent(window.location.origin + '/AuthCallback')}`,
        { method: 'GET', credentials: 'include' }
      );

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error(BLOCKED_ACCESS_MESSAGE);
        }
        const errorData = await response.json().catch(() => ({}));
        console.error('Gmail OAuth start error:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData,
          url: `${API_BASE_URL}/api/gmail/oauth/start`
        });

        if (response.status === 403) {
          throw new Error('Gmail authentication is currently unavailable. Please use email/password login or contact support.');
        }

        throw new Error(errorData.error || `Failed to start Gmail authentication (${response.status})`);
      }

      const data = await response.json();
      if (!data.authUrl) {
        throw new Error('No authorization URL received');
      }

      // Platform auth: full-page redirect in same tab (no popup). AuthCallback will navigate to /Home.
      window.location.href = data.authUrl;
    } catch (err) {
      localStorage.removeItem('gmail_oauth_user_id');
      localStorage.removeItem('gmail_oauth_auth_flow');
      setError(err.message || 'Gmail authentication failed');
      setIsLoading(false);
    }
  };

  const handleOutlookAuth = async () => {
    try {
      setIsLoading(true);
      setError('');

      const tempUserId = 'auth_' + Date.now();

      localStorage.setItem('outlook_oauth_user_id', tempUserId);
      localStorage.setItem('outlook_oauth_auth_flow', 'true');

      const response = await fetch(
        `${API_BASE_URL}/api/auth/outlook/start?redirect_uri=${encodeURIComponent(window.location.origin + '/AuthCallback')}`,
        { method: 'GET', credentials: 'include' }
      );

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error(BLOCKED_ACCESS_MESSAGE);
        }
        const errorData = await response.json().catch(() => ({}));
        console.error('Outlook OAuth start error:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData,
          url: `${API_BASE_URL}/api/outlook/oauth/start`
        });

        if (response.status === 403) {
          throw new Error('Outlook authentication is currently unavailable. Please use email/password login or contact support.');
        }

        throw new Error(errorData.error || `Failed to start Outlook authentication (${response.status})`);
      }

      const data = await response.json();
      if (!data.authUrl) {
        throw new Error('No authorization URL received');
      }

      // Platform auth: full-page redirect in same tab (no popup). AuthCallback will navigate to /Home.
      window.location.href = data.authUrl;
    } catch (err) {
      localStorage.removeItem('outlook_oauth_user_id');
      localStorage.removeItem('outlook_oauth_auth_flow');
      setError(err.message || 'Outlook authentication failed');
      setIsLoading(false);
    }
  };

  const handleManualAuth = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      setError('');

      if (isPlatformAccessBlockedEmail(email)) {
        setError(BLOCKED_ACCESS_MESSAGE);
        setIsLoading(false);
        return;
      }

      // Domain-specific tenant for signup/login (must match backend)
      const tenantId = getTenantFromEmail(email);
      const redirectUrl = location.state?.from || window.location.origin;

      if (isLogin) {
        const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Tenant-ID': tenantId,
          },
          credentials: 'include',
          body: JSON.stringify({
            email,
            password,
          }),
        });

        if (!response.ok) {
          if (response.status === 403) {
            throw new Error(BLOCKED_ACCESS_MESSAGE);
          }
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Login failed');
        }

        const data = await response.json();
        if (data.redirect) {
          window.location.href = data.redirect;
        } else {
          try {
            const sessionResponse = await fetch(`${API_BASE_URL}/api/auth/session`, {
              credentials: 'include',
            });
            if (sessionResponse.ok) {
              const sessionData = await sessionResponse.json();
              navigate(resolvePostAuthRoute(deriveCapabilities(sessionData.tenant)));
              return;
            }
          } catch (_) {
            // fall through
          }
          navigate('/Home');
        }
      } else {
        // Validate required fields for signup
        if (!companyName.trim()) {
          setError('Company name is required');
          setIsLoading(false);
          return;
        }

        const response = await fetch(`${API_BASE_URL}/api/auth/signup`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Tenant-ID': tenantId,
          },
          credentials: 'include',
          body: JSON.stringify({
            email,
            password,
            display_name: displayName,
            company_name: companyName.trim(),
          }),
        });

        if (!response.ok) {
          if (response.status === 403) {
            throw new Error(BLOCKED_ACCESS_MESSAGE);
          }
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Signup failed');
        }

        const data = await response.json();
        let sessionTenant = null;

        // Get user info to set per-user signup flag
        try {
          const userResponse = await fetch(`${API_BASE_URL}/api/auth/session`, {
            credentials: 'include'
          });
          if (userResponse.ok) {
            const sessionData = await userResponse.json();
            sessionTenant = sessionData.tenant ?? null;
            if (sessionData.user) {
              const userKey = sessionData.user.id || sessionData.user.email || 'anon';
              const NEW_SIGNUP_KEY = `new-signup:${userKey}`;
              // Set per-user signup flag
              localStorage.setItem(NEW_SIGNUP_KEY, 'true');
              // Also set legacy flag for backward compatibility
              localStorage.setItem('new-signup', 'true');
            }
          }
        } catch (e) {
          // Fallback to legacy key if we can't get user info
          localStorage.setItem('new-signup', 'true');
        }
        
        // Ensure the new_signup flag survives redirects by including it in the URL
        const postAuthRoute = resolvePostAuthRoute(deriveCapabilities(sessionTenant));
        const redirectTo = data.redirect || postAuthRoute;
        const url = new URL(redirectTo, window.location.origin);
        url.searchParams.set('new_signup', '1');
        
        window.location.href = url.toString();
      }
    } catch (err) {
      setError(err.message || `${isLogin ? 'Login' : 'Signup'} failed`);
      setIsLoading(false);
    }
  };

  // Show loading state while checking authentication
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 flex flex-col">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 flex-1 flex flex-col items-center justify-center">
        {/* Hero content section - centered */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full text-center space-y-8 mb-12"
        >
          <div className="flex justify-center">
            <img
              src={NEOGTM_LOGO_PNG_URL}
              alt="NEO GTM"
              className="h-10 mb-8"
            />
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-[#0A1A2F]">
            Selling is hard. NEO is here to help.
          </h1>
          
          <p className="text-xl text-[#5C6B7A]">
            Your AI co-pilot for GTM leaders — built to help you:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="flex items-start gap-3 p-4 rounded-lg bg-white/50 backdrop-blur-sm border border-gray-200"
            >
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#00C2C2]/10 flex items-center justify-center">
                <Target className="w-5 h-5 text-[#00C2C2]" />
              </div>
              <div>
                <h3 className="font-semibold text-[#0A1A2F] mb-1">Spot demand early</h3>
                <p className="text-sm text-[#5C6B7A]">Track renewals, extensions, and displacement opportunities</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex items-start gap-3 p-4 rounded-lg bg-white/50 backdrop-blur-sm border border-gray-200"
            >
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#00C2C2]/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-[#00C2C2]" />
              </div>
              <div>
                <h3 className="font-semibold text-[#0A1A2F] mb-1">Find the right buyers</h3>
                <p className="text-sm text-[#5C6B7A]">Champions, influencers, and decision-makers mapped</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex items-start gap-3 p-4 rounded-lg bg-white/50 backdrop-blur-sm border border-gray-200"
            >
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#00C2C2]/10 flex items-center justify-center">
                <Zap className="w-5 h-5 text-[#00C2C2]" />
              </div>
              <div>
                <h3 className="font-semibold text-[#0A1A2F] mb-1">Execute better</h3>
                <p className="text-sm text-[#5C6B7A]">Next-best actions and AI-powered playbooks</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex items-start gap-3 p-4 rounded-lg bg-white/50 backdrop-blur-sm border border-gray-200"
            >
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#00C2C2]/10 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-[#00C2C2]" />
              </div>
              <div>
                <h3 className="font-semibold text-[#0A1A2F] mb-1">Build trust</h3>
                <p className="text-sm text-[#5C6B7A]">Evidence-based insights with confidence scoring</p>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Auth form - centered below content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="w-full max-w-md mx-auto"
        >
          <Card className="border-0 shadow-2xl bg-white/95 backdrop-blur-sm">
            <CardContent className="p-6 space-y-4">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                    {error}
                  </div>
                )}

                {/* OAuth Buttons */}
                <div className="space-y-3">
                  <Button
                    onClick={handleGmailAuth}
                    disabled={isLoading}
                    variant="outline"
                    className="w-full h-12 border-2 hover:bg-gray-50 font-semibold"
                  >
                    <Mail className="w-5 h-5 mr-2" />
                    Continue with Gmail
                    {isLoading && <Loader2 className="w-4 h-4 ml-2 animate-spin" />}
                  </Button>

                  <Button
                    onClick={handleOutlookAuth}
                    disabled={isLoading}
                    variant="outline"
                    className="w-full h-12 border-2 hover:bg-gray-50 font-semibold"
                  >
                    <Mail className="w-5 h-5 mr-2" />
                    Continue with Outlook
                    {isLoading && <Loader2 className="w-4 h-4 ml-2 animate-spin" />}
                  </Button>
                </div>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-[#5C6B7A]">or</span>
                  </div>
                </div>

                {/* Manual Auth Form */}
                <form onSubmit={handleManualAuth} className="space-y-4">
                  {!isLogin && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="displayName">Full Name</Label>
                        <Input
                          id="displayName"
                          type="text"
                          placeholder="John Doe"
                          value={displayName}
                          onChange={(e) => setDisplayName(e.target.value)}
                          required={!isLogin}
                          disabled={isLoading}
                          className="h-12"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="companyName">
                          Company Name <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="companyName"
                          type="text"
                          placeholder="Acme Corp"
                          value={companyName}
                          onChange={(e) => setCompanyName(e.target.value)}
                          required={!isLogin}
                          disabled={isLoading}
                          className="h-12"
                        />
                      </div>
                    </>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={isLoading}
                      className="h-12"
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password">Password</Label>
                      {isLogin && (
                        <Link to="/ForgotPassword" className="text-sm font-medium text-[#00C2C2] hover:underline">
                          Forgot password?
                        </Link>
                      )}
                    </div>
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={isLoading}
                      className="h-12"
                      minLength={8}
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isLoading || (!isLogin && (!displayName.trim() || !companyName.trim()))}
                    className="w-full h-12 bg-[#00C2C2] hover:bg-[#00A3A3] text-white font-semibold"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        {isLogin ? 'Signing in...' : 'Creating account...'}
                      </>
                    ) : (
                      <>
                        {isLogin ? 'Sign In' : 'Create Account'}
                      </>
                    )}
                  </Button>
                </form>

                <div className="text-center pt-2">
                  <button
                    onClick={() => {
                      setIsLogin(!isLogin);
                      setError('');
                      setPassword('');
                    }}
                    className="text-sm font-bold text-[#00C2C2] hover:underline"
                    disabled={isLoading}
                  >
                    {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
                  </button>
                </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Footer */}
      <div className="text-center text-sm text-[#5C6B7A] pb-8">
        <p>© 2026 GTM Intelligence Limited</p>
      </div>
    </div>
  );
}
